import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Filters, IOnChangeParameters, Provider } from '../../../packages/Filters';

import { ApplyFiltersButtonContainer } from './ApplyFiltersButton';
import { EMetroTravelType, IJsonQuery } from '../../repositories/common/json_query';
import { FiltersSection } from '../../components/FiltersSection';
import { IAppState, TCurrentLocation } from '../../common/state';
import { SaveSearchButton } from './SaveSearchButton';
import { TTag } from '../../filters/state/tags';
import { TagsContainer } from '../../filters/components/tags/container';
import { changeJsonQuery } from '../../filters/state/json_query';
import { hasDistrictsSelector, hasHighwaysSelector, hasMetroSelector } from './utils/selectors';
import { makeSearch } from '../../filters/state/search';
import { openDirectionsModal } from '../../filters/state/directions';
import { openDistrictSwitcher } from '../../filters/state/districts/popup';
import { openLocationSwitcher } from '../../filters/state/location_switcher';
import { openMetroModal } from '../../filters/state/metro/toggle_modal';
import { trackAdvancedFiltersSearchClick, trackClearClick, trackShowAdvancedFiltersClick } from './tracking';
import { trackOpenDistrictsModalEvent } from '../../filters/components/filters/mainFilters/tracking';
import { trackOpenGeoModal } from '../../filters/components/location_switcher_button/tracking';
import { useApplicationContext } from '../../serp/containers/ApplicationContext';
import { IMakeRequest } from '../../utils/request';

export const FiltersContainer: React.FC = () => {
  const dispatch = useDispatch();
  const appContext = useApplicationContext();
  const makeRequest = useSelector<IAppState, IMakeRequest>(state => state.makeRequest);
  const jsonQuery = useSelector<IAppState, IJsonQuery>(state => state.filters.jsonQuery);
  const currentLocation = useSelector<IAppState, TCurrentLocation>(state => state.filters.currentLocation);
  const queryString = useSelector<IAppState, string | undefined>(state => state.filters.queryString) || '';
  const tags = useSelector<IAppState, TTag[]>(state => state.filters.tags);
  const hasUnderground = useSelector(hasMetroSelector);
  const hasDistricts = useSelector(hasDistrictsSelector);
  const hasHighways = useSelector(hasHighwaysSelector);

  const [isAdvancedFiltersOpen, setAdvancedFiltersOpen] = React.useState<boolean>(false);
  const updateFiltersHeightCb = React.useRef<() => void | undefined>();
  const areTagsAvailable =
    tags.length > 0 ||
    (jsonQuery.only_foot && jsonQuery.only_foot.value !== EMetroTravelType.Any) ||
    (jsonQuery.foot_min && jsonQuery.foot_min.value.lte);

  const handleChange = React.useCallback(
    (parameters: IOnChangeParameters) => {
      dispatch(changeJsonQuery(parameters));
    },
    [dispatch],
  );

  const handleOpenGeoPopup = React.useCallback(() => {
    trackOpenGeoModal();
    dispatch(openLocationSwitcher());
  }, [dispatch]);

  const handleOpenUndergroundPopup = React.useCallback(() => {
    dispatch(openMetroModal());
  }, [dispatch]);

  const handleOpenDistrictPopup = React.useCallback(() => {
    trackOpenDistrictsModalEvent();
    dispatch(openDistrictSwitcher());
  }, [dispatch]);

  const handleOpenHighwaysPopup = React.useCallback(() => {
    dispatch(openDirectionsModal());
  }, [dispatch]);

  const handleAdvancedFiltersOpen = React.useCallback(() => {
    trackShowAdvancedFiltersClick();
    setAdvancedFiltersOpen(true);
  }, []);

  const handleAdvancedFiltersClose = React.useCallback(() => {
    setAdvancedFiltersOpen(false);
  }, []);

  const handleApply = React.useCallback(() => {
    dispatch(makeSearch());
  }, [dispatch]);

  const handleApplyClick = React.useCallback(() => {
    trackAdvancedFiltersSearchClick(queryString);
  }, [queryString]);

  const handleClearClick = React.useCallback(() => {
    trackClearClick();
    dispatch({
      type: 'filters/tags/ALL_TAGS_REMOVED',
    });
  }, []);

  const setUpdateHeightCb = React.useCallback((cb: () => void) => {
    updateFiltersHeightCb.current = cb;
  }, []);

  React.useEffect(() => {
    // при изменении кол-ва тегов, запускаем пересчет мин-высоты плавающих фильтров
    if (updateFiltersHeightCb.current) {
      updateFiltersHeightCb.current();
    }
  }, [tags]);

  return (
    <Provider
      jsonQuery={jsonQuery}
      currentLocation={currentLocation}
      appContext={{
        ...appContext,
        makeRequest,
      }}
      onClear={handleClearClick}
      onApplyClick={handleApplyClick}
      onAdvancedFiltersOpen={handleAdvancedFiltersOpen}
      onAdvancedFiltersClose={handleAdvancedFiltersClose}
      onChange={handleChange}
      onApply={handleApply}
      hasUnderground={hasUnderground}
      hasDistricts={hasDistricts}
      hasHighways={hasHighways}
      onDistrictPopupOpen={handleOpenDistrictPopup}
      onGeoPopupOpen={handleOpenGeoPopup}
      onUndergroundPopupOpen={handleOpenUndergroundPopup}
      onHighwaysPopupOpen={handleOpenHighwaysPopup}
    >
      <FiltersSection setUpdateHeightCb={setUpdateHeightCb}>
        <Filters
          savedSearch={<SaveSearchButton />}
          applyButton={<ApplyFiltersButtonContainer isAdvancedFiltersOpen={isAdvancedFiltersOpen} />}
          tags={areTagsAvailable && <TagsContainer />}
        />
      </FiltersSection>
    </Provider>
  );
};
